import { useQuery } from '@tanstack/react-query';
import { api, type MatchCardV2Response } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// Match verification checklist (Lisa, 2026-07-22).
//
// The match card is the busiest page in the portal — rosters, map veto, up to
// 24 upload slots, OCR digestion, score flags, consensus cleanup, confirm
// scores, admin transition. An admin landing on a DISPUTED card has no way to
// tell WHICH stage they are at or what is still owed. This is the same device
// the org and competition go-live checklists use (useOrgChecklist /
// useCompChecklist -> SetupProgressStrip), pointed at the verification journey.
//
// Same discipline as those two: the hook reads the SAME react-query keys the
// page's own panels already read (['matchcard', matchId, compId] and
// ['scoreFlags', matchId, compId, 'OPEN'] — ScoreFlagInbox.tsx:21), so the
// strip can never drift from what the sections themselves show, and it resumes
// correctly wherever an admin left off.
//
// Derivation is a PURE function (deriveMatchVerifyChecklist) so the stage logic
// is testable without mounting a 2900-line page.
//
// TWO ENDINGS, NOT ONE — the distinction this strip exists to make legible:
//   • `confirmed` tracks the RESULT status (match.status === 'CONFIRMED') —
//     this is what actually reaches standings and feeds the next stage.
//   • `verified`  tracks the CARD status (cardStatus VERIFIED/FORGED) — a
//     separate lifecycle, the admin's sign-off on the card itself.
// They are routinely confused, and confusing them has already cost this
// codebase a live incident: a FORGED card whose result never reached standings
// (see matchCardV2.ts's FORGE_REQUIRES_CONFIRM scar). Hence `warning`.
// ─────────────────────────────────────────────────────────────────────────────

export type MatchVerifyItemId =
  | 'rosters' | 'maps' | 'screenshots' | 'digested' | 'flags' | 'signoff' | 'confirmed' | 'verified';

export type MatchVerifyItem = {
  id: MatchVerifyItemId;
  label: string;
  done: boolean;
  detail: string;
  /** Informational only — never counted toward ready/total, never blocks.
   *  Mirrors useCompChecklist's `rules` item: a legitimate skip is not a
   *  failure, and colouring it as one trains admins to ignore the strip. */
  optional?: boolean;
  /** On-page anchor the strip scrolls to (useSectionHighlight). */
  sectionId?: string;
};

export type MatchVerifyChecklist = {
  items: MatchVerifyItem[];
  readyCount: number;
  totalCount: number;
  /** Set when the card's two lifecycles disagree in a way that strands the
   *  result. Rendered loud — an all-green strip on a stranded card is exactly
   *  the failure mode this is here to prevent. */
  warning?: string;
};

export interface MatchVerifyInput {
  cardStatus: string;
  /** match.status — the RESULT lifecycle. 'CONFIRMED' is what reaches standings. */
  resultStatus?: string;
  clan1RosterSize: number;
  clan2RosterSize: number;
  vetoStatus?: 'OPEN' | 'LOCKED';
  vetoPickCount: number;
  summary: MatchCardV2Response['summary'];
  hasOcrBatch: boolean;
  openFlagCount: number;
  /** Derived server-side (shared/matchSignoff.ts) — undefined on a response
   *  cached from before sign-off shipped, which reads as "not signed". */
  signoffState?: 'AWAITING_BOTH' | 'AWAITING_ONE' | 'READY';
}

const VERIFIED_CARD_STATUSES = new Set(['VERIFIED', 'FORGED']);

export function deriveMatchVerifyChecklist(input: MatchVerifyInput): MatchVerifyChecklist {
  const {
    cardStatus, resultStatus, clan1RosterSize, clan2RosterSize,
    vetoStatus, vetoPickCount, summary, hasOcrBatch, openFlagCount, signoffState,
  } = input;

  const rostersDone = clan1RosterSize > 0 && clan2RosterSize > 0;

  // Maps are done if the veto locked OR maps got picked anyway (auto-pick at
  // the deadline never sets LOCKED but does produce picks).
  const mapsDone = vetoStatus === 'LOCKED' || vetoPickCount > 0;

  // A MISSED slot is a resolved decision, not a pending upload — counting it as
  // outstanding would strand the card on a no-show forever.
  const slotsAccountedFor = summary.slotsSubmitted + summary.slotsMissed;
  const slotsPlanned = summary.slotsExpected > 0;
  const screenshotsDone = slotsPlanned && slotsAccountedFor >= summary.slotsExpected;

  // MANUAL is the OCR-token-runout fallback: scores come in by hand, so there
  // is nothing to digest. Leaving this red would tell the admin to run
  // something that cannot run.
  const isManual = cardStatus === 'MANUAL';
  const digestedDone = hasOcrBatch || isManual;

  // F9 (Lisa, 2026-07-23): "5, why is it orange?" — flags are only ASSESSABLE
  // once there is something to have flagged. On a DRAFT card there are no
  // slots, no uploads and no digestion, so both counts are 0 and this item
  // used to render DONE (accent orange, SetupProgressStrip.tsx:70) from the
  // instant the match was created — the sole reason a brand-new card read
  // "1/7 READY" instead of "0/7". A gate that cannot be reached yet must
  // never render as satisfied. Gated on the SAME digestedDone the 'digested'
  // item uses (which already carries the MANUAL fallback), so the two can
  // never disagree about whether scores have landed.
  const flagsDone = digestedDone && openFlagCount === 0 && summary.slotsFlagged === 0;
  const confirmedDone = resultStatus === 'CONFIRMED';
  const verifiedDone = VERIFIED_CARD_STATUSES.has(cardStatus);

  const items: MatchVerifyItem[] = [
    {
      id: 'rosters',
      label: 'Squads locked',
      done: rostersDone,
      // F8 (Lisa, 2026-07-23): "2 teams confirmed the match but tick 1 says
      // it's not". Roster snapshots are written by ONE thing — match card
      // init — and by nothing anybody "confirms" anywhere. A bare "0 v 0"
      // reads as a live count waiting to fill; it actually means the card has
      // never been built. Say that.
      detail: clan1RosterSize === 0 && clan2RosterSize === 0
        ? 'card not built yet'
        : `${clan1RosterSize} v ${clan2RosterSize}`,
      sectionId: 'match-rosters',
    },
    {
      id: 'maps',
      label: 'Maps set',
      done: mapsDone,
      detail: vetoStatus === 'LOCKED' ? 'veto locked'
        : vetoPickCount > 0 ? `${vetoPickCount} picked`
        : 'optional — auto-pick',
      optional: true,
      sectionId: 'match-veto',
    },
    {
      id: 'screenshots',
      label: 'Screenshots in',
      done: screenshotsDone,
      // F6: slotsExpected arrives already reduced by games the series never
      // reached, so say WHY it shrank — an operator who saw "0/24 in" at init
      // and "0/16 in" after a 2-0 would otherwise reasonably think slots had
      // gone missing.
      detail: !slotsPlanned ? 'not planned yet'
        : [
            `${summary.slotsSubmitted}/${summary.slotsExpected} in`,
            summary.slotsMissed > 0 ? `${summary.slotsMissed} missed` : '',
            summary.slotsVoid ? `${summary.slotsVoid} not needed` : '',
          ].filter(Boolean).join(' · '),
      sectionId: 'match-slots',
    },
    {
      id: 'digested',
      label: 'OCR digested',
      done: digestedDone,
      detail: isManual ? 'manual entry' : hasOcrBatch ? 'batch complete' : 'not run',
      sectionId: 'match-digestion',
    },
    {
      id: 'flags',
      label: 'Flags cleared',
      done: flagsDone,
      // Two distinct things an admin must look at; name whichever is
      // outstanding rather than collapsing them into one meaningless count.
      detail: openFlagCount > 0 ? `${openFlagCount} open`
        : summary.slotsFlagged > 0 ? `${summary.slotsFlagged} slots flagged`
        : digestedDone ? 'clear'
        // Says "there is nothing to judge yet" rather than a green-looking
        // "clear" on a card where nothing has been submitted (F9).
        : 'not checked yet',
      sectionId: 'match-flags',
    },
    {
      id: 'signoff',
      label: 'Both sides signed',
      done: signoffState === 'READY',
      // Names how many are outstanding so the operator knows who to chase,
      // rather than a bare "not done".
      detail: signoffState === 'READY' ? 'agreed'
        : signoffState === 'AWAITING_ONE' ? '1 side outstanding'
        : 'awaiting both sides',
      sectionId: 'match-signoff',
    },
    {
      id: 'confirmed',
      label: 'Scores confirmed',
      done: confirmedDone,
      detail: confirmedDone ? 'in standings' : 'not in standings',
      sectionId: 'match-confirm',
    },
    {
      id: 'verified',
      label: 'Verified',
      done: verifiedDone,
      detail: cardStatus,
      sectionId: 'match-transition',
    },
  ];

  const blocking = items.filter(i => !i.optional);

  return {
    items,
    readyCount: blocking.filter(i => i.done).length,
    totalCount: blocking.length,
    ...(verifiedDone && !confirmedDone
      ? { warning: 'Card is verified but the result has not reached standings — confirm scores.' }
      : {}),
  };
}

/**
 * Hook form — reads the SAME cache the match card's own panels use, so the
 * strip cannot drift from the sections it summarises.
 *
 * `enabled` lets the caller mount this only for people who can actually act on
 * it (the match card gates the strip on canManageScores). A checklist of
 * chores shown to someone with no controls is just noise.
 */
export function useMatchVerifyChecklist(
  matchId: string,
  compId: string,
  enabled = true,
): MatchVerifyChecklist | null {
  const { data } = useQuery({
    queryKey: ['matchcard', matchId, compId],
    queryFn: () => api.matchCardGet(matchId, compId),
    enabled: enabled && !!matchId && !!compId,
  });
  // Same key + filter ScoreFlagInbox.tsx:21 already uses — shared cache, so the
  // count here and the list there are always the same number.
  const { data: flagData } = useQuery({
    queryKey: ['scoreFlags', matchId, compId, 'OPEN'],
    queryFn: () => api.listScoreFlags(matchId, compId, 'OPEN'),
    enabled: enabled && !!matchId && !!compId,
  });

  if (!data) return null;
  const match = data.match;

  return deriveMatchVerifyChecklist({
    cardStatus: match.cardStatus ?? 'DRAFT',
    resultStatus: match.status,
    clan1RosterSize: (match.clan1RosterSnapshot ?? []).length,
    clan2RosterSize: (match.clan2RosterSnapshot ?? []).length,
    vetoStatus: match.mapVeto?.status,
    vetoPickCount: match.mapVeto?.picks?.length ?? 0,
    summary: data.summary,
    hasOcrBatch: !!match.ocrBatchSummary,
    openFlagCount: (flagData?.flags ?? []).length,
    signoffState: data.signoff?.state,
  });
}
